import React, { useState, useEffect, useRef } from "react";
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { MeshoptDecoder } from "three/examples/jsm/libs/meshopt_decoder.module";
import * as THREE from "three";

/**
 * Line-drawing viewer. Solids are flat, near-opaque faces in the sheet ground
 * colour so they occlude what is behind them; every silhouette and crease is
 * drawn as an edge line in `plan` (or `ink` on the blueprint theme). The result
 * is a hidden-line drawing, not a shaded render.
 */

/** Share of the binding frame axis the drawing should span. */
const FILL = 0.84;
/** Half-angle of the hero sway, radians (about 6 degrees). */
const SWAY = 0.105;
/** How long lifting the tracing paper takes, seconds. */
const REVEAL_SECONDS = 0.45;
/** One full sway cycle, seconds. */
const SWAY_PERIOD = 26;
/** Vertices sampled per model for camera fitting. The frame is set by the
 *  silhouette, which many vertices bound, so a few thousand is plenty. */
const SAMPLE_BUDGET = 6000;

const DEFAULT_VIEW = [0.62, 0.42, 0.66];
const CAMERA = { position: [3, 2, 3.4], fov: 42 };
const GL = { alpha: true, antialias: true };
const DPR = [1, 1.75];
const RESIZE = { debounce: { scroll: 50, resize: 200 } };
const FULL = { width: "100%", height: "100%" };
const CANVAS_STYLE = { ...FULL, background: "transparent" };

const Y_AXIS = new THREE.Vector3(0, 1, 0);
/** Orbit zoom range, in the model's normalised units. */
const ZOOM = { min: 0.4, max: 40 };
/** Longest frame the animations will integrate; a demand-mode canvas can be
 *  idle for seconds between frames and must not jump. */
const MAX_STEP = 1 / 30;

/** Exact distance at which every point stays inside FILL of the frustum
 *  looking along `f` with frame axes `u` (up) and `r` (right). */
function solveDistance(points, target, f, u, r, tanV, tanH) {
  let dist = 0;
  for (let i = 0; i < points.length; i += 3) {
    const px = points[i] - target.x;
    const py = points[i + 1] - target.y;
    const pz = points[i + 2] - target.z;
    const depth = px * f.x + py * f.y + pz * f.z;
    const v = Math.abs(px * u.x + py * u.y + pz * u.z);
    const h = Math.abs(px * r.x + py * r.y + pz * r.z);
    const d = Math.max(depth + v / tanV, depth + h / tanH);
    if (d > dist) dist = d;
  }
  return dist || 1;
}

/**
 * Frame the camera on the model's real projected silhouette.
 *
 * Three things have to be right or the drawing sits stranded in an empty box.
 * The bounds must come from the vertices, not the bounding sphere (far too
 * loose for the long, flat volumes IFC scenes usually are) and not the eight
 * box corners (the axis-aligned hull is much larger than the geometry inside
 * it). And the aim point must be the centre of what the *projection* covers,
 * not the centre of the model: seen obliquely, a flat plate projects well off
 * its own centre, and a fit that stays symmetric about the model centre spends
 * half the frame on nothing. So centre the target on the projected bounds,
 * then solve the exact distance at which no point escapes FILL of the frustum
 * on either axis, which puts the binding axis at FILL and never clips.
 *
 * Returns the aim point, which is also where OrbitControls has to orbit about.
 */
function fitCameraToPoints(camera, points, seed, viewDir, aspect, sway) {
  const forward = viewDir.clone();
  if (forward.lengthSq() < 1e-8) forward.fromArray(DEFAULT_VIEW);
  forward.normalize();

  const right = new THREE.Vector3().crossVectors(Y_AXIS, forward);
  if (right.lengthSq() < 1e-8) right.set(1, 0, 0);
  right.normalize();
  const up = new THREE.Vector3().crossVectors(forward, right).normalize();

  const tanFull = Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2);
  const tanV = tanFull * FILL;
  const tanH = tanV * Math.max(aspect, 0.0001);
  const target = seed.clone();

  let dist = solveDistance(points, target, forward, up, right, tanV, tanH);
  // Slide the aim point onto the centre of the projected bounds, then re-solve.
  // Four passes is well past convergence for these models.
  for (let pass = 0; pass < 4; pass++) {
    let xMin = Infinity;
    let xMax = -Infinity;
    let yMin = Infinity;
    let yMax = -Infinity;
    for (let i = 0; i < points.length; i += 3) {
      const px = points[i] - target.x;
      const py = points[i + 1] - target.y;
      const pz = points[i + 2] - target.z;
      const dc = dist - (px * forward.x + py * forward.y + pz * forward.z);
      if (dc <= 1e-4) continue;
      const sx = (px * right.x + py * right.y + pz * right.z) / dc;
      const sy = (px * up.x + py * up.y + pz * up.z) / dc;
      if (sx < xMin) xMin = sx;
      if (sx > xMax) xMax = sx;
      if (sy < yMin) yMin = sy;
      if (sy > yMax) yMax = sy;
    }
    if (!isFinite(xMin) || !isFinite(yMin)) break;
    target
      .addScaledVector(right, ((xMin + xMax) / 2) * dist)
      .addScaledVector(up, ((yMin + yMax) / 2) * dist);
    dist = solveDistance(points, target, forward, up, right, tanV, tanH);
  }

  if (sway) {
    // The hero sways a few degrees either side of its bearing instead of
    // turning all the way round: a full turn needs a camera far enough back
    // for the broadside view, which leaves the end-on view at a third of the
    // frame. Fit the union of the sway arc so the drawing fills the frame at
    // every bearing it will actually show.
    const f = new THREE.Vector3();
    const r = new THREE.Vector3();
    const u = new THREE.Vector3();
    for (let i = -3; i <= 3; i++) {
      if (i === 0) continue;
      f.copy(forward).applyAxisAngle(Y_AXIS, (i / 3) * SWAY);
      r.crossVectors(Y_AXIS, f);
      if (r.lengthSq() < 1e-8) r.set(1, 0, 0);
      r.normalize();
      u.crossVectors(f, r).normalize();
      dist = Math.max(dist, solveDistance(points, target, f, u, r, tanV, tanH));
    }
  }

  camera.position.copy(target).addScaledVector(forward, dist);
  // The model is normalised to a 2-unit extent, so fixed planes cover every
  // zoom OrbitControls allows (see ZOOM below).
  camera.near = 0.01;
  camera.far = 200;
  camera.lookAt(target);
  camera.updateProjectionMatrix();
  return target;
}

/**
 * Edge lines for one mesh. A child of an InstancedMesh is drawn once, at the
 * parent's own matrix, so for instanced geometry the edges of every placement
 * are baked into a single line geometry in the parent's local space.
 */
function edgesFor(mesh) {
  const edges = new THREE.EdgesGeometry(mesh.geometry, 20);
  if (!mesh.isInstancedMesh) return edges;
  const src = edges.attributes.position;
  const out = new Float32Array(src.count * 3 * mesh.count);
  const m = new THREE.Matrix4();
  const v = new THREE.Vector3();
  let k = 0;
  for (let i = 0; i < mesh.count; i++) {
    mesh.getMatrixAt(i, m);
    for (let j = 0; j < src.count; j++) {
      v.fromBufferAttribute(src, j).applyMatrix4(m);
      out[k++] = v.x;
      out[k++] = v.y;
      out[k++] = v.z;
    }
  }
  edges.dispose();
  const merged = new THREE.BufferGeometry();
  merged.setAttribute("position", new THREE.BufferAttribute(out, 3));
  return merged;
}

/** Free every GPU resource a loaded scene holds. Materials are shared across
 *  meshes, so they are collected first and disposed once. */
function disposeScene(root) {
  const geometries = new Set();
  const materials = new Set();
  root.traverse((obj) => {
    if (obj.geometry) geometries.add(obj.geometry);
    if (obj.material) {
      (Array.isArray(obj.material) ? obj.material : [obj.material]).forEach(
        (mat) => materials.add(mat),
      );
    }
  });
  geometries.forEach((g) => g.dispose());
  materials.forEach((mat) => {
    if (mat.map) mat.map.dispose();
    mat.dispose();
  });
}

/** Sample world-space vertices once, so re-fits on resize stay cheap. */
function sampleWorldPoints(root) {
  const meshes = [];
  let total = 0;
  root.traverse((child) => {
    if (child.isMesh && child.geometry && child.geometry.attributes.position) {
      meshes.push(child);
      total += child.geometry.attributes.position.count;
    }
  });
  const stride = Math.max(1, Math.ceil(total / SAMPLE_BUDGET));
  const out = [];
  const v = new THREE.Vector3();
  const m = new THREE.Matrix4();
  const push = () => out.push(v.x, v.y, v.z);
  for (const mesh of meshes) {
    const pos = mesh.geometry.attributes.position;
    if (mesh.isInstancedMesh) {
      // An instance's placement is not in matrixWorld. Its bounding-box
      // corners are a conservative stand-in for its vertices.
      if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox();
      const { min, max } = mesh.geometry.boundingBox;
      for (let i = 0; i < mesh.count; i++) {
        mesh.getMatrixAt(i, m);
        m.premultiply(mesh.matrixWorld);
        for (let c = 0; c < 8; c++) {
          v.set(
            c & 1 ? max.x : min.x,
            c & 2 ? max.y : min.y,
            c & 4 ? max.z : min.z,
          ).applyMatrix4(m);
          push();
        }
      }
      continue;
    }
    for (let i = 0; i < pos.count; i += stride) {
      v.fromBufferAttribute(pos, i).applyMatrix4(mesh.matrixWorld);
      push();
    }
  }
  return new Float32Array(out);
}

function readTokens() {
  if (typeof window === "undefined") {
    return { surface: "#ffffff", edge: "#2e56a6" };
  }
  const cs = getComputedStyle(document.documentElement);
  const dark = document.documentElement.getAttribute("data-theme") === "dark";
  const pick = (name, fallback) =>
    (cs.getPropertyValue(name) || fallback).trim() || fallback;
  return {
    surface: pick("--surface", "#ffffff"),
    // On paper the linework is plan blue; on blueprint it is white ink.
    edge: dark ? pick("--ink", "#eaf0fa") : pick("--plan", "#2e56a6"),
  };
}

function useSheetTokens() {
  const [tokens, setTokens] = useState(readTokens);
  useEffect(() => {
    const obs = new MutationObserver(() => {
      const next = readTokens();
      setTokens((prev) =>
        prev.surface === next.surface && prev.edge === next.edge ? prev : next,
      );
    });
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => obs.disconnect();
  }, []);
  return tokens;
}

function matches(query) {
  return (
    typeof window !== "undefined" &&
    Boolean(window.matchMedia) &&
    window.matchMedia(query).matches
  );
}

/** True while the element is on screen. Offscreen viewers stop animating. */
function useInView(ref) {
  const [inView, setInView] = useState(true);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const obs = new IntersectionObserver(([entry]) =>
      setInView(entry.isIntersecting),
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref]);
  return inView;
}

function Model({
  modelPath,
  tokens,
  viewDir,
  sway,
  reveal,
  revealColours,
  onReady,
}) {
  const revealRef = useRef(0);
  const [scene, setScene] = useState(null);
  const { camera, size, invalidate, controls } = useThree();
  const paintRef = useRef(null);
  const pointsRef = useRef(null);
  const centerRef = useRef(new THREE.Vector3());

  const aspect = size.width / Math.max(size.height, 1);

  /** Re-frame around whatever direction the camera currently looks from, so a
   *  resize keeps the user's orbit and only corrects the distance. Kept in a
   *  ref so that a change of `sway` or `controls` never re-fits under the
   *  user's hand; only a new scene or a new canvas size does. */
  const refitRef = useRef(null);
  refitRef.current = () => {
    const points = pointsRef.current;
    if (!points) return;
    const dir = camera.position.clone().sub(centerRef.current);
    const target = fitCameraToPoints(
      camera,
      points,
      centerRef.current,
      dir.lengthSq() < 1e-8 ? viewDir : dir,
      aspect,
      sway,
    );
    centerRef.current.copy(target);
    if (controls && controls.target) {
      controls.target.copy(target);
      controls.update();
    }
    invalidate();
  };

  useEffect(() => {
    let cancelled = false;
    let loaded = null;
    // The models ship meshopt-compressed (see public/assets/*.glb).
    const loader = new GLTFLoader();
    loader.setMeshoptDecoder(MeshoptDecoder);
    loader.load(
      modelPath,
      (gltf) => {
        const root = gltf.scene;
        if (cancelled) {
          disposeScene(root);
          return;
        }
        loaded = root;

        // One drawing material pair for the whole model; the theme flip and
        // the reveal then touch two objects, not two per mesh.
        const face = new THREE.MeshBasicMaterial({
          color: new THREE.Color(tokens.surface),
          transparent: true,
          opacity: 0.9,
          polygonOffset: true,
          polygonOffsetFactor: 2,
          polygonOffsetUnits: 2,
          side: THREE.DoubleSide,
        });
        const lineMat = new THREE.LineBasicMaterial({
          color: new THREE.Color(tokens.edge),
          transparent: true,
        });
        const colours = [];

        const meshes = [];
        root.traverse((child) => {
          if (child.isMesh && child.geometry) meshes.push(child);
        });
        meshes.forEach((child) => {
          child.add(new THREE.LineSegments(edgesFor(child), lineMat));
          if (revealColours) {
            // The source colours (a chloride heat map, say) stay on the mesh
            // itself, unlit so they read as flat as the drawing. The drawing
            // is an overlay laid on top like tracing paper; a hover lifts it.
            const orig = child.material;
            const colour = new THREE.MeshBasicMaterial({
              color: orig && orig.color ? orig.color.clone() : 0xffffff,
              map: orig && orig.map ? orig.map : null,
              vertexColors: Boolean(child.geometry.attributes.color),
              side: THREE.DoubleSide,
              transparent: true,
              opacity: 0,
              // The drawing overlay sits on identical geometry; if this mesh
              // wrote depth the overlay would fail the depth test and the
              // faces would never fill. Hidden until the reveal starts.
              depthWrite: false,
              visible: false,
            });
            child.material = colour;
            colours.push(colour);
            const overlay = child.isInstancedMesh
              ? new THREE.InstancedMesh(child.geometry, face, child.count)
              : new THREE.Mesh(child.geometry, face);
            if (child.isInstancedMesh) {
              overlay.instanceMatrix = child.instanceMatrix;
            }
            overlay.renderOrder = 1;
            child.add(overlay);
          } else {
            child.material = face;
          }
        });
        paintRef.current = { face, lineMat, colours };

        // Centre on the origin and normalise scale so near/far stay sane
        // whatever units the source model was authored in. Scale applies
        // before position in the local matrix, so the re-centring offset is
        // expressed in scaled units too; the world centre is then the origin.
        const raw = new THREE.Box3().setFromObject(root);
        const size3 = raw.getSize(new THREE.Vector3());
        const center = raw.getCenter(new THREE.Vector3());
        const k = 2 / (Math.max(size3.x, size3.y, size3.z) || 1);
        root.scale.setScalar(k);
        root.position.set(-center.x * k, -center.y * k, -center.z * k);
        root.updateMatrixWorld(true);

        pointsRef.current = sampleWorldPoints(root);
        centerRef.current.set(0, 0, 0);
        camera.position.copy(viewDir);

        // The mount effect below does the first fit.
        setScene(root);
        if (onReady) onReady();
      },
      undefined,
      (err) => {
        console.error(`ThreeViewer: could not load ${modelPath}`, err);
        if (!cancelled && onReady) onReady();
      },
    );
    return () => {
      cancelled = true;
      if (loaded) disposeScene(loaded);
      loaded = null;
      paintRef.current = null;
      pointsRef.current = null;
      setScene(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelPath, camera]);

  // Fit on load and whenever the canvas is resized.
  useEffect(() => {
    if (scene) refitRef.current();
  }, [scene, size.width, size.height]);

  // Repaint in place when the theme flips: no reload, no flash.
  useEffect(() => {
    const paint = paintRef.current;
    if (!paint) return;
    paint.face.color.set(tokens.surface);
    paint.lineMat.color.set(tokens.edge);
    invalidate();
  }, [tokens.surface, tokens.edge, scene, invalidate]);

  // In demand mode nothing draws until asked; a hover change asks.
  useEffect(() => {
    invalidate();
  }, [reveal, invalidate]);

  // Sway the model, not the camera, so OrbitControls never fights it; and
  // lift or lay the tracing paper when the pointer enters or leaves.
  // The frameloop clock resets whenever it flips between always and demand,
  // and a demand canvas can hand over seconds of delta after idling, so both
  // animations integrate their own clamped step.
  const phaseRef = useRef(0);
  useFrame((_, delta) => {
    if (!scene) return;
    const dt = Math.min(delta, MAX_STEP);
    if (sway) {
      phaseRef.current += (dt / SWAY_PERIOD) * Math.PI * 2;
      scene.rotation.y = Math.sin(phaseRef.current) * SWAY;
    }
    const goal = reveal ? 1 : 0;
    const cur = revealRef.current;
    const paint = paintRef.current;
    if (paint && Math.abs(goal - cur) > 0.001) {
      const next = cur + (goal - cur) * Math.min(1, (dt / REVEAL_SECONDS) * 3);
      revealRef.current = Math.max(0, Math.min(1, next));
      const t = revealRef.current;
      const e = t * t * (3 - 2 * t);
      paint.face.opacity = 0.9 * (1 - e);
      paint.lineMat.opacity = 1 - 0.75 * e;
      paint.colours.forEach((c) => {
        c.opacity = e;
        c.visible = e > 0.002;
      });
      invalidate();
    }
  });

  if (!scene) return null;
  return <primitive object={scene} />;
}

/**
 * @param viewDir  [x, y, z] direction the camera looks from, model-relative.
 * @param autoRotate  sway gently until the user drags (off under reduced motion).
 * @param revealColours  keep the source colours under the drawing; hover lifts it.
 */
function ThreeViewer({
  modelPath,
  viewDir = DEFAULT_VIEW,
  autoRotate = false,
  revealColours = false,
  onReady,
}) {
  const tokens = useSheetTokens();
  const wrapRef = useRef(null);
  const inView = useInView(wrapRef);
  const [swaying, setSwaying] = useState(
    () => autoRotate && !matches("(prefers-reduced-motion: reduce)"),
  );
  // Until the visitor has orbited the drawing, the wheel scrolls the page
  // (the viewers sit in the reading flow; a zoom trap there is the classic
  // inline-3D mistake). Once they have taken hold of it, the wheel zooms.
  const [engaged, setEngaged] = useState(false);
  const draggingRef = useRef(false);
  const [hover, setHover] = useState(false);
  const sway = swaying && inView;
  const reveal = revealColours && hover;
  const [dir] = useState(() => new THREE.Vector3().fromArray(viewDir));

  // `change` also fires when a resize re-fits the camera; only a change that
  // happens under the pointer counts as the visitor taking over.
  const onChange = () => {
    if (!draggingRef.current) return;
    setSwaying(false);
    setEngaged(true);
  };

  return (
    <div
      ref={wrapRef}
      style={FULL}
      onPointerEnter={() => setHover(true)}
      onPointerLeave={() => setHover(false)}
    >
      <Canvas
        camera={CAMERA}
        dpr={DPR}
        gl={GL}
        resize={RESIZE}
        frameloop={sway ? "always" : "demand"}
        style={CANVAS_STYLE}
      >
        <Model
          modelPath={modelPath}
          tokens={tokens}
          viewDir={dir}
          sway={sway}
          reveal={reveal}
          revealColours={revealColours}
          onReady={onReady}
        />
        <OrbitControls
          makeDefault
          enableZoom={engaged}
          enablePan={false}
          minDistance={ZOOM.min}
          maxDistance={ZOOM.max}
          enableRotate
          onStart={() => {
            draggingRef.current = true;
          }}
          onEnd={() => {
            draggingRef.current = false;
          }}
          onChange={onChange}
          minPolarAngle={Math.PI / 6}
          maxPolarAngle={Math.PI / 1.6}
        />
      </Canvas>
    </div>
  );
}

export default ThreeViewer;
